import isUndefined from 'lodash/isUndefined';
import { Requester } from '@zg-rentals/particles-js-requester';

import { AppSelectors, EnvSelectors, UserSelectors } from 'app/shared/selectors';
import CookiesActions from 'app/shared/actions/CookiesActions';
import errorUtils from 'app/shared/utils/errorUtils';
import { PROXY_PATH, RENTAL_GROWTH_GRAPHQL_API_V1_PREFIX } from 'app/shared/constants/RouteConstants';
import ErrorRMW from 'app/shared/model/ErrorRMW';

export default function requester({
    method,
    endpoint,
    queryParams = {},
    body = {},
    proxyPath,
    hostname,
    skipClientProxy = false,
} = {}) {
    return (dispatch, getState) => {
        const state = getState();
        let zgApi;

        const apiLog = global.log.child({
            name: 'requesterLog',
            method,
            endpoint,
        });

        // if hostname is passed in, use that for zgAPi address.
        // useful for properly routing requests and passing on
        // cookies when using requester to interface with other
        // zg-rentals services outside of default zgApi.
        if (hostname) {
            zgApi = hostname;
        } else if (__SERVER__) {
            zgApi = EnvSelectors.makeZgApi(state);
        }

        let url;

        if (__CLIENT__ && skipClientProxy) {
            url = `${window.location.protocol}//${window.location.host}/${endpoint}`;
        } else {
            url = Requester.getPath({
                path: endpoint,
                proxyPath: isUndefined(proxyPath) ? PROXY_PATH : proxyPath,
                zgApi,
            });
        }

        const originalServerSideRequestCookies = UserSelectors.makeOriginalServerSideRequestCookies(state);

        // eslint-disable-next-line camelcase
        const headers = {
            Accept: 'application/json,text/html',
            'X-Build-Id': __BUILD_NUMBER__ || 42,
        };

        if (__CLIENT__) {
            headers['x-csrf-token'] = AppSelectors.makeCsrfToken(state);
        }

        // if graphql endpoint
        if (endpoint.indexOf(RENTAL_GROWTH_GRAPHQL_API_V1_PREFIX) !== -1) {
            // accept text/plain content
            headers['Content-Type'] = 'text/plain';
        }

        const options = {
            method,
            url,
            data: body,
            params: queryParams,
            headers,
        };

        apiLog.debug(options, 'requester options');

        const request = new Requester(options, {
            cookieObj: originalServerSideRequestCookies,
            userAgent: AppSelectors.makeUserAgent(state),
            ip: AppSelectors.makeIp(state),
            brand: AppSelectors.makeBrand(state),
            logger: apiLog,
        });

        return request
            .send()
            .then((response = {}) => {
                const { data = {} } = response;

                // update server side request cookies based on api response
                // used by renderer.js to return the correct cookies in header of o.g. request
                if (response.headers['set-cookie']) {
                    dispatch(
                        CookiesActions.updateUserCookies({
                            apiResponseServerSideRequestCookies: response.headers['set-cookie'],
                        }),
                    );
                }

                return data;
            })
            .catch((err) => {
                const data = err && err.response ? err.response.data : undefined;
                // fix for 10,000 byte limit
                err.config = '';
                err.request = '';
                err.response = '';

                apiLog.error(err);

                const { errorCopy, rawErrorsCopy } = errorUtils.formatError(data);

                // normalize error
                const normalizedErrorCopy = ErrorRMW(errorCopy);

                return {
                    success: false,
                    error: normalizedErrorCopy,
                    errors: rawErrorsCopy,
                    response: data ? data.response : {},
                };
            });
    };
}
